/**
 * @file PipelinesPage.tsx
 * @description Воронки сделок магазина — раздел «Настроек» (phase09;
 * phase19.31 — минимум по SC50: заголовок, кнопки, фильтры, русские подписи).
 *
 * Заголовок — в верхней панели (`CabinetPageHeader`), «+ Воронка» — главное
 * действие (право `SHOP_MANAGE_CRM_CONFIG`). Фильтр по виду — пилюли (SC45)
 * с подписями из кода («Розница», «Корпоративные»), «Архивные» —
 * пилюля-переключатель, а не нативный флажок. Список — компактные строки
 * (`PipelineList`), служебные описания стартовых данных не показываются.
 */

import { Plus, Search, Workflow } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";

import { useHasPermission } from "../../../../auth/hooks";
import { PipelineList } from "../../../../components/crm/pipelines/PipelineList";
import { PipelineEditDialog } from "../../../../components/crm/PipelineEditDialog";
import { Button } from "../../../../components/ui/button";
import { CabinetPageHeader } from "../../../../components/ui/CabinetPageHeader";
import { FilterChips, FilterToggleChips, type FilterChip } from "../../../../components/ui/FilterChips";
import { ListEmpty, ListError, ListSkeleton } from "../../../../components/ui/ListStates";
import { ApiError } from "../../../../lib/api-client";
import { PIPELINE_KIND_SHORT, listPipelines, type PipelineDto, type PipelineKind } from "../../../../lib/api-crm";

type KindFilter = "all" | PipelineKind;

const KIND_FILTERS: FilterChip<KindFilter>[] = [
  { value: "all", label: "Все" },
  { value: "SALES_B2C", label: PIPELINE_KIND_SHORT.SALES_B2C },
  { value: "SALES_B2B", label: PIPELINE_KIND_SHORT.SALES_B2B },
  { value: "RETURNS", label: PIPELINE_KIND_SHORT.RETURNS },
];

export function PipelinesPage() {
  const { slug } = useParams<{ slug: string }>();
  const canConfig = useHasPermission("SHOP_MANAGE_CRM_CONFIG");
  const [items, setItems] = useState<PipelineDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [kind, setKind] = useState<KindFilter>("all");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState<PipelineDto | "new" | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listPipelines({ kind: kind === "all" ? undefined : kind, includeInactive });
      setItems(res.items);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить воронки");
    } finally {
      setLoading(false);
    }
  }, [kind, includeInactive]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const filtered = kind !== "all" || includeInactive;

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader
        title="Воронки сделок"
        subtitle="Стадии, по которым идут сделки до заказа"
        action={
          canConfig ? (
            <Button variant="brand" onClick={() => setEditing("new")}>
              <Plus className="size-4" />
              Воронка
            </Button>
          ) : undefined
        }
      />

      <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">
        <FilterChips aria-label="Вид воронки" items={KIND_FILTERS} value={kind} onChange={setKind} />
        <FilterToggleChips
          aria-label="Архивные воронки"
          items={[{ key: "archived", label: "Архивные", pressed: includeInactive, onToggle: () => setIncludeInactive((v) => !v) }]}
        />
      </div>

      {error && items.length === 0 ? (
        <ListError message={error} onRetry={() => void refresh()} />
      ) : loading && items.length === 0 ? (
        <ListSkeleton rows={3} compact />
      ) : items.length === 0 ? (
        <ListEmpty
          filtered={filtered}
          spec={
            filtered
              ? { icon: Search, title: "Нет воронок по выбранному отбору", onReset: () => { setKind("all"); setIncludeInactive(false); } }
              : { icon: Workflow, title: "Воронок сделок ещё нет", description: canConfig ? "Создайте первую кнопкой «+ Воронка»" : undefined }
          }
        />
      ) : (
        <PipelineList
          pipelines={items}
          stagesHref={(p) => `/shop/${slug}/crm/pipelines/${p.id}`}
          onEdit={canConfig ? (p) => setEditing(p) : undefined}
        />
      )}

      {editing ? (
        <PipelineEditDialog
          mode={editing === "new" ? "create" : "edit"}
          pipeline={editing === "new" ? undefined : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void refresh();
          }}
        />
      ) : null}
    </div>
  );
}
